import type { HistoryAttribute } from "@/types/api/history";
import type {
  Allergy,
  ConsultationReason,
  DiabeticPatient,
  Disease,
  Gender,
  GravityState,
  GravityStates,
  Pathology,
  Patient,
  SmokingPatient,
  ToothBrushHairType,
  ToothBrushType,
  WomenHealth,
} from "@/types/api/patient";
import type { Feeling } from "@/types/api/perioquiz";
import type { User } from "@/types/api/user";

export const getName = (userOrPatient: User | Patient): string => {
  return userOrPatient.firstName + " " + userOrPatient.lastName;
};

export const getAge = (apiBirthDate: Date): number => {
  const now = new Date();
  const birthDate = new Date(apiBirthDate);
  let age = now.getFullYear() - birthDate.getFullYear();
  const m = now.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const getRiskFactors = (patient: Patient): GravityStates => {
  return {
    toothbrush: patient.toothState,
    smoking: patient.smokingState,
    diabetic: patient.diabetState,
    stress: patient.stressState,
    geneticTendency: patient.geneticState,
  };
};

const sortPatientPathologies = (first: Pathology, second: Pathology) => {
  if (first === "SYNDROME") {
    return -2;
  }
  if (first === "HEART") {
    return -1;
  }
  if (second === "SYNDROME") {
    return 2;
  }
  if (second === "HEART") {
    return 1;
  }
  return 0;
};

export const getDiseases = (patient: Patient) => {
  return {
    disease: patient.disease,
    associatedPathologies: patient.associatedPathologies.sort(
      sortPatientPathologies
    ),
    diabet: patient.diabetic,
  };
};

type GravityStates = {
  smoking: GravityState;
  geneticTendency: GravityState;
  toothbrush: GravityState;
  diabetic: GravityState;
  stress: GravityState;
};

export type Risk = keyof GravityStates;

export const getRiskColor = (
  strength: GravityState
): "error" | "warning" | "success" | "neutral" => {
  switch (strength) {
    case "UNSTABLE":
      return "error";
    case "TREATMENT":
      return "warning";
    case "STABLE":
      return "success";
    case "NONE":
      return "neutral";
    default:
      return "neutral";
  }
};

export const getRiskLabel = (risk: Risk) => {
  switch (risk) {
    case "smoking":
      return "Tabac";
    case "geneticTendency":
      return "Prédisposition";
    case "toothbrush":
      return "Brossage";
    case "diabetic":
      return "Diabète";
    case "stress":
      return "Stress";
  }
};

export const consultationOptions: Array<{
  value: ConsultationReason;
  label: string;
}> = [
  { value: "BLEEDING", label: "Saignement gingival" },
  { value: "MOBILITY", label: "Mobilités/migrations dentaires" },
  { value: "PAIN", label: "Douleur" },
  { value: "HALITOSE", label: "Halitose" },
  { value: "AESTHETIC", label: "Esthétique" },
  { value: "CONTROL", label: "Contrôle" },
  { value: "ADRESSED", label: "Adressé" },
];

export const getConsultationLabel = (
  consultationReason: ConsultationReason
) => {
  return consultationOptions.find((elem) => elem.value === consultationReason)
    ?.label;
};

export const getToothBrushType = (
  toothBrushType: ToothBrushType | undefined
) => {
  const result = toothBrushTypeOptions.find(
    (elem) => elem.value === toothBrushType
  );
  return result ? result.label : "";
};

export const getToothBrushHairType = (
  toothBrushHairType: ToothBrushHairType | undefined
) => {
  const result = toothBrushHairTypeOptions.find(
    (elem) => elem.value === toothBrushHairType
  );
  return result ? result.label.toLowerCase() : "";
};

export const getSmokingPatientLabel = (smokingPatient: SmokingPatient) => {
  return smokingOptions.find((elem) => elem.value === smokingPatient)?.label;
};

export const getDiabetLabel = (diabeticPatient: DiabeticPatient) => {
  return diabeticOptions.find((elem) => elem.value === diabeticPatient)?.label;
};

export const getHealthLabel = (value: Allergy | Pathology | WomenHealth) => {
  const pathologyOption = pathologiesOptions.find(
    (elem) => elem.value === value
  );
  if (pathologyOption) {
    return pathologyOption.label;
  }
  const allergyOption = allergiesOptions.find((elem) => elem.value === value);
  if (allergyOption) {
    return allergyOption.label;
  }
  return womenHealthOptions.find((elem) => elem.value === value)?.label;
};

export const getDiseaseLabel = (value: Disease) => {
  return diseaseOptions.find((elem) => elem.value === value)?.label;
};

export const getDiseasesLabel = (
  value: Disease | Pathology | DiabeticPatient
) => {
  const diseaseOption = diseaseOptions.find((elem) => elem.value === value);
  if (diseaseOption) {
    return diseaseOption.label;
  }
  const pathologyOption = pathologiesOptions.find(
    (elem) => elem.value === value
  );
  if (pathologyOption) {
    return pathologyOption.label;
  }
  return "Diabète";
};

export const genderOptions: Array<{
  value: Gender;
  label: string;
}> = [
  { value: "FEMALE", label: "Femme" },
  { value: "MALE", label: "Homme" },
  { value: "OTHER", label: "Autre" },
];

export const toothBrushTypeOptions: Array<{
  value: ToothBrushType;
  label: string;
}> = [
  { value: "MANUAL", label: "Manuelle" },
  { value: "ELECTRICAL", label: "Électrique" },
  { value: "BOTH", label: "Les deux" },
];

export const toothBrushHairTypeOptions: Array<{
  value: ToothBrushHairType;
  label: string;
}> = [
  { value: "SOFT", label: "Souple" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HARD", label: "Dure" },
];

export const smokingOptions: Array<{
  value: SmokingPatient;
  label: string;
}> = [
  { value: "FALSE", label: "Non" },
  { value: "TRUE", label: "Oui" },
  { value: "PREVIOUS", label: "Ancien fumeur" },
];

export const diabeticOptions: Array<{
  value: DiabeticPatient;
  label: string;
}> = [
  { value: "NONE", label: "Non" },
  { value: "ONE", label: "Type 1" },
  { value: "TWO", label: "Type 2" },
  { value: "GESTATIONAL", label: "Gestationnel" },
];

export const stressedOptions: Array<{
  value: number;
  label: string;
}> = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
  { value: 6, label: "6" },
  { value: 7, label: "7" },
  { value: 8, label: "8" },
  { value: 9, label: "9" },
  { value: 10, label: "10" },
];

export const pathologiesOptions: Array<{
  value: Pathology;
  label: string;
}> = [
  { value: "SYNDROME", label: "Syndrome métabolique" },
  { value: "OBESITY", label: "Obésité" },
  { value: "HEART", label: "Maladie cardio-vasculaire" },
  { value: "RESPIRATORY", label: "Maladie respiratoire" },
  { value: "KIDNEY", label: "Maladie rénale" },
  { value: "POLYARTHRITIS", label: "Polyarthrite rhumatoïde" },
  { value: "IMMUNE", label: "Immunodépression" },
  { value: "ALZHEIMER", label: "Alzheimer" },
  { value: "OTHER", label: "Autre" },
];

export const allergiesOptions: Array<{
  value: Allergy;
  label: string;
}> = [
  { value: "PENICILLIN", label: "Pénicilline" },
  { value: "POVIDONE", label: "Povidone iodée" },
  { value: "CODEIN", label: "Codéine" },
  { value: "OTHER", label: "Autre" },
];

export const womenHealthOptions: Array<{
  value: WomenHealth;
  label: string;
}> = [
  { value: "PREGNANT", label: "Enceinte" },
  { value: "MENOPAUSE", label: "Ménopausée" },
  { value: "SOPK", label: "SOPK" },
  { value: "ENDOMETRIOSIS", label: "Endométriose" },
  { value: "OTHER", label: "Autre" },
];

export const stateOptions: Array<{
  value: GravityState;
  label: string;
}> = [
  { value: "UNSTABLE", label: "Instable" },
  { value: "TREATMENT", label: "En stabilisation" },
  { value: "STABLE", label: "Stable" },
];

export const getGravityStateLabel = (gravityState: GravityState): string => {
  return (
    stateOptions.find((elem) => elem.value === gravityState)?.label ?? "Inconnu"
  );
};

export const diseaseOptions: Array<{
  value: Disease;
  label: string;
}> = [
  { value: "GINGIVITIS", label: "Gingivite" },
  { value: "PERIODONTITIS", label: "Parodontite" },
  { value: "PERIIMPLANTITIS", label: "Péri-implantite" },
];

export const formatPhoneNumber = (phoneNumber: string): string => {
  let formattedNumber = `${phoneNumber[0]}${phoneNumber[1]}${phoneNumber[2]} `;
  const n = phoneNumber.length;
  for (let i = 3; i < n - 1; i += 2) {
    formattedNumber += `${phoneNumber[i]}${phoneNumber[i + 1]} `;
  }
  return formattedNumber;
};

export const formatFullDate = (date: Date): string => {
  return `${date.getDate()} ${date.toLocaleString("fr", { month: "long" })} ${date.getFullYear()}`;
};

export const feelingOptions: Array<{
  value: Feeling;
  label: string;
}> = [
  { value: "PAIN", label: "une douleur au niveau des gencives" },
  { value: "MOVEMENT", label: "des dents qui bougent" },
  { value: "BREATH", label: "une mauvaise haleine" },
];

export const getFeelingLabels = (feelings: Array<Feeling>): Array<string> => {
  const labels: Array<string> = [];
  feelings.forEach((feeling) => {
    labels.push(
      feelingOptions.find((option) => option.value === feeling)?.label ??
        "Inconnu"
    );
  });
  return labels;
};

export const getHistoryAttributeLabel = (
  attribute: HistoryAttribute
): string => {
  switch (attribute) {
    case "CIGARETTES":
      return "Tabac";
    case "DIABETES":
      return "Diabète";
    case "STRESS":
      return "Stress";
    case "IP":
      return "IP";
    case "IS":
      return "IS";
    default:
      return "Inconnu";
  }
};
